import { getInventoryType } from 'src/utils/helper';

import { getData } from 'src/lib/firebase';
import { getCountryData } from 'src/assets/data';
import { numberFormatLocale } from 'src/locales';

import { getUserProfile } from './users-ssr';

// ----------------------------------------------------------------------

const DEFAULT_STOCK = 100;

const listParamsValidate = (param) => (['search', 'favorites'].includes(param) ? param : null);

export const FIELDS_QUERY = ['country', 'shipping', 'category', 'sellerUID', 'priceRange'];

export const PLACEHOLDER_IMAGE = [
  { src: '/assets/core/placeholder-image.webp', type: 'image/webp' },
];

export const getOrderBy = (field) =>
  ({
    newest: [{ field: 'createdAt', value: 'desc' }],
    priceDesc: [{ field: 'priceSale', value: 'desc' }],
    priceAsc: [{ field: 'priceSale', value: 'asc' }],
    featured: [
      { field: 'totalReviews', value: 'desc' },
      { field: 'totalRating', value: 'desc' },
    ],
  })[field] ?? [];

export const getFilters = (searchParams, additionalFields = []) => {
  const filters = [];

  if (!searchParams) return filters;

  searchParams.forEach((value, field) => {
    if (value && [...FIELDS_QUERY, ...additionalFields].includes(field)) {
      switch (field) {
        case 'priceRange': {
          const [min, max] = value.split('-').map(Number);
          if (min) filters.push({ field: 'priceSale', operator: '>=', value: min });
          if (max) filters.push({ field: 'priceSale', operator: '<=', value: max });
          break;
        }
        default:
          filters.push({ field, operator: '==', value });
      }
    }
  });

  return filters;
};

export const promotionDataFormatter = (promotion) => {
  const coverUrl = promotion.images?.find(({ type }) => type?.includes('image'))?.src;
  const images = (promotion.images ?? PLACEHOLDER_IMAGE).map((image) => ({
    ...image,
    ...(image.type?.includes('video') && { poster: coverUrl }),
  }));
  const numberFormat =
    promotion.numberFormat ??
    getCountryData(promotion.country)?.numberFormat ??
    numberFormatLocale();

  return {
    ...promotion,
    images,
    coverUrl,
    numberFormat,
    name: promotion.name,
    code: promotion.code,
    price: promotion.price,
    sku: promotion.sku ?? '',
    taxes: promotion.taxes ?? 0,
    sizes: promotion.sizes ?? [],
    category: promotion.category,
    newLabel: promotion.newLabel,
    saleLabel: promotion.saleLabel,
    priceSale: promotion.priceSale,
    colors: promotion.colors ?? [],
    shipping: !!promotion.shipping,
    description: promotion.description,
    publish: promotion.publish ?? 'YES',
    totalLikes: promotion.totalLikes ?? 0,
    totalSales: promotion.totalSales ?? 0,
    id: promotion.objectID ?? promotion.id,
    totalShared: promotion.totalShared ?? 0,
    totalRating: promotion.totalRating ?? 0,
    totalReviews: promotion.totalReviews ?? 0,
    shortDescription: promotion.shortDescription,
    quantity: promotion.quantity ?? DEFAULT_STOCK,
    available: promotion.available ?? DEFAULT_STOCK,
    sellerUID: promotion.sellerUID ?? promotion.owner,
    createdAt: promotion.createdAt?.toDate() ?? promotion.create,
    inventoryType: getInventoryType(promotion.available ?? DEFAULT_STOCK),
    gender: Array.isArray(promotion.gender) ? promotion.gender : ['Unisex'],
    tags: Array.isArray(promotion.tags) ? promotion.tags : [promotion.category],
  };
};

export async function getProducts({
  firestore,
  limit = 20,
  uid = 'undefined',
  searchParams = {},
  defaultFilter = true,
}) {
  const keys = Object.keys(searchParams);
  const sortBy = searchParams?.sortBy ?? 'featured';
  const list = listParamsValidate(searchParams?.list) ?? 'shop';

  const filters =
    list === 'shop'
      ? getFilters({
          forEach: (callback) => keys.forEach((key) => callback(searchParams[key], key)),
        })
      : [];

  const products =
    list === 'search'
      ? []
      : await getData({
          path: list === 'favorites' ? `users/${uid}/favorites` : 'promotions',
          formatter: (promotion) =>
            list === 'favorites' ? getProduct(promotion.id) : promotionDataFormatter(promotion),
          defaultData: [],
          options: {
            orderBy: getOrderBy(list === 'favorites' ? 'newest' : sortBy),
            limit: Math.min(Math.max(limit, 0), 20),
            where: [
              ...(defaultFilter && list === 'shop'
                ? [{ field: 'publish', operator: '==', value: 'YES' }]
                : []),
              ...filters,
            ],
          },
          firestore,
        });

  return { list, sortBy, products };
}

export async function getProduct(id, sellerData) {
  if (!id) return {};

  const promotion = await getData({ path: `promotions/${id}`, formatter: promotionDataFormatter });

  return {
    ...promotion,
    seller: sellerData && promotion.sellerUID ? await getUserProfile(promotion.sellerUID) : [],
  };
}
